/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { RegisterOptions } from "react-hook-form/dist/types/validator";
import styled from "styled-components";
import { Input, Textarea } from "@nordcloud/gnui";
import { InlineEditForm } from "~/components/InlineEdit/InlineEditForm";
import { StyledOutsideLink } from "~/components/Styled/Common";
import { isValidUrlSchema, itHasNotWhitespace } from "~/tools";

const StyledTextarea = styled(Textarea)`
  width: 25rem;
  height: 5rem;
`;

type Props = {
  name: string;
  defaultValue?: string | null;
  required?: boolean;
  link?: boolean;
  validation?: RegisterOptions;
  textArea?: boolean;
  onSubmit: (value: string) => Promise<void>;
  canEdit?: boolean;
  width?: string;
  href?: string;
};

function InlineEditText({
  name,
  defaultValue,
  required = false,
  link = false,
  validation,
  textArea = false,
  href = "",
  onSubmit,
  canEdit,
  width = "100%",
}: Props) {
  const { register, errors, handleSubmit } = useForm({ mode: "onChange" });

  const submit = (values: any) => onSubmit(values[name]);

  const fullValidation = {
    required: { value: required, message: "This field is required" },
    ...validation,
  };

  if (link) {
    fullValidation.validate = (text: string) =>
      (itHasNotWhitespace(text) && isValidUrlSchema.safeParse(text).success) ||
      "Please enter a valid link";
  }

  const display =
    (href && defaultValue) || defaultValue?.startsWith("https://") ? (
      <StyledOutsideLink href={href || defaultValue}>
        {defaultValue}
      </StyledOutsideLink>
    ) : (
      defaultValue || "–"
    );

  const Component = textArea ? StyledTextarea : Input;

  return (
    <InlineEditForm
      onSubmit={handleSubmit(submit)}
      display={display}
      canEdit={canEdit}
      error={errors[name]?.message}
      width={width}
    >
      <Component
        name={name}
        type="text"
        small
        defaultValue={defaultValue || ""}
        status={errors[name] && "danger"}
        ref={register(fullValidation)}
      />
    </InlineEditForm>
  );
}

export { InlineEditText };
